import { Button, TYPE } from '@common/Button/Button';
import { Icon, IconColor, IconID } from '@common/Icon/Icon';
import { WidenImage } from '@common/Widen/WidenImage';
import type { Teasers } from '@generated/graphql';
import {
  E_CHAIN_EXPERT_TEASER_ID,
  getLocale,
  WIDEN_ECHAIN_EXPERT_TEASER_IMAGE_HEIGHT,
  WIDEN_ECHAIN_EXPERT_TEASER_IMAGE_WIDTH,
} from '@libs/constants';
import { WidenUtil } from '@libs/widen-utils';
import { useOnlineToolsTeaserQuery } from '@product/EChainExpertTeaserBox/useOnlineToolsTeaserQuery';
import { useTranslation } from 'next-i18next';
import Image from 'next/image';
import { useRouter } from 'next/router';
import React, { useEffect, useState } from 'react';
import tailwindConfig from '../../../tailwind.config';
import { IGUS_REWRITE } from '@libs/redirect.helper';
import { useBreakpoints } from '@hooks/useBreakpoints';

export const EChainExpertTeaserBox: React.FC = () => {
  const router = useRouter();
  const locale = getLocale(router);
  const height: number = WIDEN_ECHAIN_EXPERT_TEASER_IMAGE_HEIGHT;

  const { data: teasers, isLoading } = useOnlineToolsTeaserQuery();
  const { t } = useTranslation('product/eChainExpertTeaserBox');

  const [teaser, setTeaser] = useState<Teasers | undefined>();

  const breakpoint = useBreakpoints();

  useEffect(() => {
    if (!isLoading && teasers?.length) {
      const eChainTeaser = teasers.find(item => item.id === E_CHAIN_EXPERT_TEASER_ID);
      if (eChainTeaser) {
        setTeaser(eChainTeaser);
      }
    }
  }, [isLoading, teasers]);

  const isWidenImage = teaser?.widen_url ? WidenUtil.isWidenImageUrl(teaser.widen_url) : false;
  const widenShortCode = teaser?.widen_url ? WidenUtil.extractWidenImageId(teaser.widen_url) : undefined;
  const gray50color = tailwindConfig.theme.extend.colors.gray[50].replace('#', '');
  const href = IGUS_REWRITE(teaser?.link ?? ':w-0', locale);

  return isLoading || !teaser ? null : (
    <div className="bg-gray-50 p-8 pb-5 mt-8 w-full" data-testid="e-chain-expert-teaser">
      <div className="mb-5 ml-4 leading-8 font-bold text-lg">{teaser.label}</div>
      {teaser.text && <div dangerouslySetInnerHTML={{ __html: teaser.text }} className="text-sm mt-3 ml-4" />}
      <a href={href}>
        <div className="mr-4 text-center mt-10 mb-5 pl-3">
          {isWidenImage && widenShortCode && (
            <WidenImage
              height={height}
              width={
                breakpoint.lg && !breakpoint.xl
                  ? WIDEN_ECHAIN_EXPERT_TEASER_IMAGE_WIDTH.lg
                  : WIDEN_ECHAIN_EXPERT_TEASER_IMAGE_WIDTH.default
              }
              color={gray50color}
              shortCode={widenShortCode}
              filename="teaser_image.webp"
              quality={100}
            />
          )}
          {!isWidenImage && teaser.image && (
            <Image
              unoptimized
              width={150}
              height={150}
              src={teaser.widen_url ?? ''}
              alt={teaser?.label?.toString() ?? ''}
              style={{
                objectFit: 'contain',
              }}
            />
          )}
        </div>
      </a>
      <Button
        href={href}
        external
        text={t('E_CHAIN_EXPERT_TEASER_BUTTON')}
        icon={<Icon size={10} color={IconColor.WHITE} id={IconID.EXTERNAL_LINK} />}
        type={TYPE.SECONDARY}
      />
    </div>
  );
};
